/**
 * Image Compression Utility
 * Compresses images before upload to handle server size limits
 */

/**
 * Compress an image file to meet size requirements
 * @param {File} file - Original image file
 * @param {number} maxSizeMB - Maximum size in MB (default: 0.9MB for 1MB server limit)
 * @param {number} maxWidthOrHeight - Maximum width/height in pixels (default: 1920)
 * @param {number} quality - JPEG quality 0-1 (default: 0.8)
 * @returns {Promise<File>} Compressed image file
 */
export const compressImage = async (
  file,
  maxSizeMB = 0.9,
  maxWidthOrHeight = 1920,
  quality = 0.8
) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        try {
          // Calculate new dimensions
          let { width, height } = img;
          
          if (width > maxWidthOrHeight || height > maxWidthOrHeight) {
            if (width > height) {
              height = (height / width) * maxWidthOrHeight;
              width = maxWidthOrHeight;
            } else {
              width = (width / height) * maxWidthOrHeight;
              height = maxWidthOrHeight;
            }
          }
          
          // Create canvas
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Try different quality levels if needed
          const attemptCompression = (currentQuality) => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to compress image'));
                  return;
                }
                
                const compressedSizeMB = blob.size / (1024 * 1024);
                
                // If still too large and quality can be reduced, try again
                if (compressedSizeMB > maxSizeMB && currentQuality > 0.3) {
                  attemptCompression(currentQuality - 0.1);
                  return;
                }
                
                // Create a new File object from the blob
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                
                console.log('Image compressed:', {
                  originalSize: `${(file.size / 1024).toFixed(2)} KB`,
                  compressedSize: `${(compressedFile.size / 1024).toFixed(2)} KB`,
                  reduction: `${(((file.size - compressedFile.size) / file.size) * 100).toFixed(1)}%`,
                  quality: currentQuality.toFixed(2)
                });
                
                resolve(compressedFile);
              },
              'image/jpeg',
              currentQuality
            );
          };
          
          attemptCompression(quality);
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Check if an image needs compression
 * @param {File} file - Image file to check
 * @param {number} maxSizeMB - Maximum size in MB
 * @returns {boolean} True if file needs compression
 */
export const needsCompression = (file, maxSizeMB = 0.9) => {
  const fileSizeMB = file.size / (1024 * 1024);
  return fileSizeMB > maxSizeMB;
};

/**
 * Validate and optionally compress an image file
 * @param {File} file - Image file to validate/compress
 * @param {Object} options - Configuration options
 * @returns {Promise<{file: File, wasCompressed: boolean, error: string|null}>}
 */
export const validateAndCompressImage = async (file, options = {}) => {
  const {
    maxSizeMB = 0.9,
    maxWidthOrHeight = 1920,
    quality = 0.8,
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  } = options;
  
  // Validate file type
  if (!file.type.startsWith('image/')) {
    return {
      file: null,
      wasCompressed: false,
      error: 'Only image files are allowed'
    };
  }
  
  // Check if file needs compression
  if (needsCompression(file, maxSizeMB)) {
    try {
      const compressedFile = await compressImage(file, maxSizeMB, maxWidthOrHeight, quality);
      
      // If compressed file is still too large, return error
      if (compressedFile.size > maxSizeMB * 1024 * 1024) {
        return {
          file: null,
          wasCompressed: false,
          error: `Image is too large. Even after compression, the file size (${(compressedFile.size / 1024).toFixed(0)} KB) exceeds the ${maxSizeMB}MB limit. Please use a smaller image or reduce its dimensions.`
        };
      }
      
      return {
        file: compressedFile,
        wasCompressed: true,
        error: null
      };
    } catch (error) {
      return {
        file: null,
        wasCompressed: false,
        error: `Failed to compress image: ${error.message}`
      };
    }
  }
  
  // File is within size limit, no compression needed
  return {
    file,
    wasCompressed: false,
    error: null
  };
};

export default {
  compressImage,
  needsCompression,
  validateAndCompressImage
};
